import React from 'react'
import { Col, Container, Row, Tab } from 'react-bootstrap'
import { Tabs } from '../../components/Tabs/Tabs'
import { PresetTab } from './AIFeaturePresets/PresetTab'
import { useNavigate, useParams } from 'react-router-dom'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import PrizeBadge from '../../components/ScopedBlock/PrizeBadge.svg?url'
import { MeetingTemplateTab } from './MeetingTemplates/MeetingTemplateTab'
import { Settings } from './Settings/Settings'
import { AIFormTab } from './AIForms/AIFormTab'
import { EmailTypesTab } from './EmailTypes/EmailTypesTab'
import { DocumentTypesTab } from './DocumentTypes/DocumentTypesTab'
import { Templates } from './Templates/Templates'
import { useIsFeatureAdvertisementEnabled } from '../../common/useIsFeatureAdvertisementEnabled'
import { useRedirectHiddenTab } from '../../common/useRedirectHiddenTab'
import { SchedulingSetupTab } from './Scheduling/SchedulingSetupTab'

export const Setup: React.FC = () => {
  const { activeTab = 'meeting-types' } = useParams()
  const { hasQuota, isFetching: isQuotaFetching } = useZocksQuota()

  const navigate = useNavigate()

  const changeTab = (key: string | null) => {
    if (key) {
      navigate(`/workspace/setup/${key}`, { replace: true })
    }
  }

  const formEnabled = hasQuota('ai.formFill.value')
  const emailEnabled = hasQuota('integration.emailEnabled.value')
  const documentProcessingEnabled = hasQuota('ai.documentSummaryAgent.value')
  const schedulingEnabled = hasQuota('scheduling.enabled.value')
  const { isEnabled: featureAdvertisementEnabled, isFetching: isFeatureAdvertisementFetching } = useIsFeatureAdvertisementEnabled()
  const isSchedulingTabVisible = schedulingEnabled || featureAdvertisementEnabled
  const isFormTabVisible = formEnabled || featureAdvertisementEnabled
  const isEmailTypesTabVisible = emailEnabled || featureAdvertisementEnabled
  const isDocumentTypesTabVisible = documentProcessingEnabled || featureAdvertisementEnabled

  useRedirectHiddenTab({
    currentKey: activeTab,
    hiddenKeys: [
      !isEmailTypesTabVisible && 'email-types',
      !isDocumentTypesTabVisible && 'document-types',
      !isFormTabVisible && 'forms',
      !isSchedulingTabVisible && 'scheduling',
    ],
    isLoading: isFeatureAdvertisementFetching || isQuotaFetching,
    fallbackPath: '/workspace/setup/meeting-types',
  })

  const presetsTitle = <div className='d-flex align-items-center'>Meeting Presets</div>

  const formTitle = (
    <div className='d-flex align-items-center zocks-gap-0.25'>
      {!formEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Forms
    </div>
  )

  const settingsTitle = <div className='d-flex align-items-center'>Settings</div>

  const meetingTemplateTitle = <div className='d-flex align-items-center'>Meeting Types</div>

  const emailTypesTitle = (
    <div className='d-flex align-items-center zocks-gap-0.25'>
      {!emailEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Email Types
    </div>
  )

  const documentTypesTitle = (
    <div className='d-flex align-items-center zocks-gap-0.25'>
      {!documentProcessingEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Document Types
    </div>
  )

  const templatesTitle = <div className='d-flex align-items-center'>Templates</div>

  const schedulingTitle = (
    <div className='d-flex align-items-center zocks-gap-0.25'>
      {!schedulingEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Scheduling
    </div>
  )

  return (
    <Container>
      <Row className='mb-9'>
        <Col>
          <h3>Setup</h3>
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs onSelect={changeTab} activeKey={activeTab} id='setup-tabs' className='d-flex gap-2r mb-3'>
            <Tab className='mt-5' eventKey='meeting-types' title={meetingTemplateTitle}>
              <MeetingTemplateTab />
            </Tab>
            <Tab className='mt-5' eventKey='templates' title={templatesTitle}>
              <Templates />
            </Tab>
            {isEmailTypesTabVisible && (
              <Tab className='mt-5' eventKey='email-types' title={emailTypesTitle}>
                <EmailTypesTab />
              </Tab>
            )}
            {isSchedulingTabVisible && (
              <Tab className='mt-5' eventKey='scheduling' title={schedulingTitle}>
                <SchedulingSetupTab />
              </Tab>
            )}
            {isDocumentTypesTabVisible && (
              <Tab className='mt-5' eventKey='document-types' title={documentTypesTitle}>
                <DocumentTypesTab />
              </Tab>
            )}
            <Tab className='mt-5' eventKey='preset' title={presetsTitle}>
              <PresetTab />
            </Tab>
            {isFormTabVisible && (
              <Tab className='mt-5' eventKey='forms' title={formTitle}>
                <AIFormTab />
              </Tab>
            )}
            {/*<Tab className='mt-5' eventKey='custom-dictionary' title='Custom dictionary'>*/}
            {/*  <CustomDictionary />*/}
            {/*</Tab>*/}
            <Tab className='mt-5' eventKey='settings' title={settingsTitle}>
              <Settings />
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  )
}
